// hooks/useCategoryLibrary.ts
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Category, CategoryItem, CategoryItemSectioned } from '../types/category';
import { CATEGORIES, categoryContentLoaders, getCategory } from '../data/categories';

export const useCategoryLibrary = () => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryItems, setCategoryItems] = useState<Record<string, CategoryItemSectioned[]>>({});
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState<Set<string>>(new Set());

  const categories = CATEGORIES;

  // Track loaded categories in a ref to avoid stale closures
  const loadedCategoriesRef = useRef<Set<string>>(new Set());
  const loadingCategoriesRef = useRef<Set<string>>(new Set());

  // Load items for a specific category
  const loadCategoryItems = useCallback(async (categoryId: string) => {
    // Check refs first to avoid stale closure issues
    if (loadedCategoriesRef.current.has(categoryId) || loadingCategoriesRef.current.has(categoryId)) {
      return;
    }

    loadingCategoriesRef.current.add(categoryId);
    setLoadingCategories(prev => new Set(prev).add(categoryId));

    try {
      const loader = categoryContentLoaders[categoryId];
      console.log(`[CategoryLoader] Loading category: ${categoryId}, loader exists: ${!!loader}`);
      if (loader) {
        const module = await loader();
        console.log(`[CategoryLoader] Loaded module for ${categoryId}:`, module);
        const items = module.default || [];
        console.log(`[CategoryLoader] Items for ${categoryId}:`, items.length);
        
        // Mark as loaded in refs
        loadedCategoriesRef.current.add(categoryId);
        
        setCategoryItems(prev => ({
          ...prev,
          [categoryId]: items,
        }));
      }
    } catch (error) {
      console.error(`[CategoryLoader] Failed to load category ${categoryId}:`, error);
      loadedCategoriesRef.current.add(categoryId); // Mark as loaded even on error
      setCategoryItems(prev => ({
        ...prev,
        [categoryId]: [],
      }));
    } finally {
      loadingCategoriesRef.current.delete(categoryId);
      setLoadingCategories(prev => {
        const next = new Set(prev);
        next.delete(categoryId);
        return next;
      });
    }
  }, []);

  // Load items when categories are selected
  useEffect(() => {
    selectedCategories.forEach(categoryId => {
      loadCategoryItems(categoryId);
    });
  }, [selectedCategories, loadCategoryItems]);

  // Preload all categories (optional - for better UX)
  const preloadAllCategories = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all(
        Object.keys(categoryContentLoaders).map(id => loadCategoryItems(id))
      );
    } finally {
      setLoading(false);
    }
  }, [loadCategoryItems]);

  // Get all items from all loaded categories
  const allItems = useMemo(() => {
    return Object.values(categoryItems).flat();
  }, [categoryItems]);

  // Filter items based on selected categories and search query
  const items = useMemo(() => {
    let result: CategoryItemSectioned[];

    if (selectedCategories.length === 0) {
      result = allItems;
    } else {
      // Combine items from all selected categories
      result = selectedCategories.flatMap(catId => categoryItems[catId] || []);
    }

    // Deduplicate (in case items ever have multiple categories)
    const seen = new Set<string>();
    result = result.filter(item => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item =>
        item.title.toLowerCase().includes(query) ||
        item.content.toLowerCase().includes(query)
      );
    }

    // Sort by word count (ascending - smallest first)
    result = result.sort((a, b) => a.wordCount - b.wordCount);

    return result;
  }, [selectedCategories, searchQuery, categoryItems, allItems]);

  // Get items for a specific category
  const getItemsByCategory = useCallback((categoryId: string): CategoryItemSectioned[] => {
    return categoryItems[categoryId] || [];
  }, [categoryItems]);

  // Search across all categories
  const searchItems = useCallback((query: string): CategoryItemSectioned[] => {
    const lowerQuery = query.toLowerCase();
    return allItems.filter(item =>
      item.title.toLowerCase().includes(lowerQuery) ||
      item.content.toLowerCase().includes(lowerQuery)
    );
  }, [allItems]);

  // Get category by ID
  const getCategoryById = useCallback((categoryId: string): Category | undefined => {
    return getCategory(categoryId);
  }, []);

  // Get count of items in a category
  const getCategoryItemCount = useCallback((categoryId: string): number => {
    return categoryItems[categoryId]?.length || 0;
  }, [categoryItems]);

  // Check if a category is loading
  const isCategoryLoading = useCallback((categoryId: string): boolean => {
    return loadingCategories.has(categoryId);
  }, [loadingCategories]);

  // Toggle category selection (add/remove from selected categories)
  const toggleCategory = useCallback((categoryId: string) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  }, []);

  // Check if a category is currently selected
  const isCategorySelected = useCallback((categoryId: string): boolean => {
    return selectedCategories.includes(categoryId);
  }, [selectedCategories]);

  // Clear all category selections
  const clearSelection = useCallback(() => {
    setSelectedCategories([]);
  }, []);

  return {
    categories,
    items,
    allItems,
    selectedCategories,
    setSelectedCategories,
    toggleCategory,
    isCategorySelected,
    clearSelection,
    searchQuery,
    setSearchQuery,
    loading,
    loadingCategories,
    getItemsByCategory,
    searchItems,
    getCategoryById,
    getCategoryItemCount,
    isCategoryLoading,
    loadCategoryItems,
    preloadAllCategories,
  };
};

export default useCategoryLibrary;
